import { client } from '@/apollo/vue-apollo';
import { gql } from 'graphql-tag';
import axios from 'axios';

export function recipientAddTag(
	participant: string,
	legalEntity: string,
	recipient: string,
	tag: string
) {
	const payload = {
		participant: participant,
		legal_entity: legalEntity,
		recipient: recipient,
		tag: tag
	};

	return axios.post(`https://${process.env.VUE_APP_API_ENDPOINT}/operator-fe/recipient-tag`, payload, {
		headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
	});
}

export function recipientRemoveTag(
	participant: string,
	legalEntity: string,
	recipient: string,
	tag: string
) {
	const payload = {
		participant: participant,
		legal_entity: legalEntity,
		recipient: recipient,
		tag: tag
	};

	return axios.delete(`https://${process.env.VUE_APP_API_ENDPOINT}/operator-fe/recipient-tag`, {
		headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` },
		data: payload
	});
}

export function recipientEndCollection(
	participant: string,
	legalEntity: string,
	recipient: string,
	username: string,
	person: string
) {
	const note = `Collection stopped by ${username}`;
	const mutation = gql`
		mutation recipientEndCollection (
			$participant: uuid!
			$legalEntity: uuid!
			$recipient: uuid!
			$person: uuid!
			$note: String!
		) {
			update_risolto_recipient_collection (
				where: {
					_and: [
						{ participant: { _eq: $participant }}
						{ legal_entity: { _eq: $legalEntity }}
						{ recipient: { _eq: $recipient }}
						{ closed_at: { _is_null: true }}
					]
				}
				_set:{
					closed_at: "now"
					closed_by: $person
				}
			) {
				affected_rows
			}
			insert_risolto_note_one(
				object: {
					note: $note
					participant: $participant
					category: OTHER
					recipient: $recipient
					legal_entity: $legalEntity
					created_by: $person
					system_note: true
				}
			) {
				reference
			}
		}
	`;
	const variables = {
		participant: participant,
		legalEntity: legalEntity,
		recipient: recipient,
		note: note,
		person: person
	};

	return client.mutate({
		mutation: mutation,
		variables: variables
	});
}

export function recipientUploadAttachment(
	participant: string,
	legalEntity: string,
	recipient: string,
	attachmentType: string,
	name: string,
	metadata: any
) {
	const mutation = gql`
	mutation ViewsInvoiceDisputesRecipient_upload_recipient_attachment(
	  $participant: uuid!
	  $legalEntity: uuid!
	  $recipient: uuid!
	  $attachmentType: risolto_document_attachment_type_enum!
	  $name: String!
	  $metadata: jsonb!
	) {
	  insert_risolto_recipient_attachment_one(
		object: {
		  participant: $participant
		  legal_entity: $legalEntity
		  recipient: $recipient
		  attachment_type: $attachmentType
		  name: $name
		  metadata: $metadata
		}
	  ) {
		reference
	  }
	}
	`;

	return client.mutate({
		mutation,
		variables: {
			participant,
			legalEntity,
			recipient,
			attachmentType,
			name,
			metadata
		}
	});
}

export async function recipientDeleteAttachment(
	key: any
) {
	const search = gql`
	query ViewsInvoiceDisputesRecipient_find_recipient_attachment($search: jsonb!)
	{
	  risolto_recipient_attachment(
		where: {
		  metadata: { _contains: $search }
		}
	  ) {
		reference
	  }
	}
	`;

	const remove = gql`
	mutation ViewsInvoiceDisputesRecipient_delete_recipient_attachment(
	  $reference: uuid!
	) {
	  delete_risolto_recipient_attachment_by_pk(reference: $reference) {
		reference
	  }
	}
	`;

	const result = await client.query({
		query: search,
		variables: {
			search: {
				key: key
			}
		}
	});

	if (result.data.risolto_recipient_attachment.length === 0) {
		throw new Error('Attachment not found');
	}

	const reference = result.data.risolto_recipient_attachment[0].reference;

	return client.mutate({
		mutation: remove,
		variables: {
			reference
		}
	});
}

export async function recipientUpdateChannelEndpoint(
	participant: string,
	legalEntity: string,
	recipient: string,
	channel_endpoint: any
) {
	const mutation = gql`
	mutation ApiRecipient_recipientUpdateChannelEndpoint(
	  $participant: uuid!
	  $legalEntity: uuid!
	  $recipient: uuid!
	  $reference: uuid!
	  $channel: String!
	  $endpoint: String!
	  $endpoint_details: jsonb!
	  $locale: String!
	) {
		update_risolto_channel_endpoint(
			where: {
				_and: [
					{ participant: { _eq: $participant }}
					{ legal_entity: { _eq: $legalEntity }}
					{ recipient: { _eq: $recipient }}
					{ reference: { _eq: $reference }}
				]
			}
			_set: {
				channel: $channel
				endpoint: $endpoint
				endpoint_details: $endpoint_details
				locale: $locale
			}
		) {
			affected_rows
		}
	}
	`;

	return client.mutate({
		mutation,
		variables: {
			participant,
			legalEntity,
			recipient,
			reference: channel_endpoint.reference,
			channel: channel_endpoint.channel,
			endpoint: channel_endpoint.endpoint,
			endpoint_details: channel_endpoint.endpoint_details,
			locale: channel_endpoint.locale
		}
	});
}

export async function recipientUpdateContact(
	participant: string,
	legalEntity: string,
	recipient: string,
	contact: any
) {
	const mutation = gql`
	mutation ApiRecipient_recipientUpdateChannelEndpoint(
	  $participant: uuid!
	  $legalEntity: uuid!
	  $recipient: uuid!
	  $reference: uuid!
	  $contactType: uuid
	  $firstName: String!
	  $lastName: String!
	  $locale: String!
	) {
		update_risolto_contact(
			where: {
				_and: [
					{ participant: { _eq: $participant }}
					{ legal_entity: { _eq: $legalEntity }}
					{ recipient: { _eq: $recipient }}
					{ reference: { _eq: $reference }}
				]
			}
			_set: {
				contact_type: $contactType
				first_name: $firstName
				last_name: $lastName
				locale: $locale
			}
		) {
			affected_rows
		}
	}
	`;

	return client.mutate({
		mutation,
		variables: {
			participant,
			legalEntity,
			recipient,
			reference: contact.reference,
			contactType: contact.contact_type || null,
			firstName: contact.first_name,
			lastName: contact.last_name,
			locale: contact.language
		}
	});
}
